import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  UserGroupIcon,
  PhoneIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CalendarIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../services/api';

const TeamAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30'); // days
  const [selectedMetric, setSelectedMetric] = useState('leads');
  const { user } = useAuth();
  const { companyData } = useCompany();

  useEffect(() => {
    fetchAnalyticsData();
  }, [dateRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/team', {
        params: {
          dateRange
        }
      });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
      // Set empty data structure to prevent UI crashes
      setAnalyticsData({
        overview: {
          totalLeads: 0,
          leadsGrowth: 0,
          totalCustomers: 0,
          customersGrowth: 0,
          totalFollowups: 0,
          followupsGrowth: 0,
          conversionRate: 0,
          conversionGrowth: 0
        },
        teamPerformance: [],
        recentActivity: [],
        conversionFunnel: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getMetricValue = (metric, member) => {
    switch (metric) {
      case 'leads':
        return member.leads;
      case 'customers':
        return member.customers;
      case 'followups':
        return member.followups;
      case 'conversion':
        return `${member.conversionRate}%`;
      default:
        return member.leads;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'lead':
        return UserGroupIcon;
      case 'customer':
        return UsersIcon;
      case 'followup':
        return PhoneIcon;
      default:
        return UserGroupIcon;
    }
  };

  const formatChange = (value) => {
    const isPositive = value > 0;
    return (
      <span className={`inline-flex items-center text-sm font-medium ${
        isPositive ? 'text-green-600' : 'text-red-600'
      }`}>
        {isPositive ? (
          <ArrowUpIcon className="w-4 h-4 mr-1" />
        ) : (
          <ArrowDownIcon className="w-4 h-4 mr-1" />
        )}
        {Math.abs(value)}%
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading analytics..." />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 p-8 mb-8 shadow-2xl"
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>

            <div className="relative z-10 flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                  <ChartBarIcon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Team Analytics</h1>
                  <p className="text-purple-100 text-base lg:text-lg">
                    Track team performance and conversion metrics
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4 w-full lg:w-auto">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 w-full lg:w-auto"
                >
                  <option value="7" className="text-gray-900">Last 7 days</option>
                  <option value="30" className="text-gray-900">Last 30 days</option>
                  <option value="90" className="text-gray-900">Last 3 months</option>
                  <option value="365" className="text-gray-900">Last year</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Overview Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8"
          >
            {[
              {
                title: 'Total Leads',
                value: analyticsData?.overview?.totalLeads || 0,
                change: analyticsData?.overview?.leadsGrowth || 0,
                icon: UserGroupIcon,
                color: 'from-green-500 to-emerald-500'
              },
              {
                title: 'Customers',
                value: analyticsData?.overview?.totalCustomers || 0,
                change: analyticsData?.overview?.customersGrowth || 0,
                icon: UsersIcon,
                color: 'from-blue-500 to-cyan-500'
              },
              {
                title: 'Follow-ups',
                value: analyticsData?.overview?.totalFollowups || 0,
                change: analyticsData?.overview?.followupsGrowth || 0,
                icon: PhoneIcon,
                color: 'from-orange-500 to-red-500'
              },
              {
                title: 'Conversion Rate',
                value: `${analyticsData?.overview?.conversionRate || 0}%`,
                change: analyticsData?.overview?.conversionGrowth || 0,
                icon: ArrowTrendingUpIcon,
                color: 'from-purple-500 to-pink-500'
              }
            ].map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100 hover:shadow-2xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                    <div className="mt-2">
                      {formatChange(stat.change)}
                    </div>
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 lg:gap-8">
            {/* Team Performance */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="xl:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-500 to-blue-500 px-4 sm:px-6 py-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-3 sm:space-y-0">
                  <h3 className="text-lg sm:text-xl font-bold text-white">Team Performance</h3>
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedMetric}
                      onChange={(e) => setSelectedMetric(e.target.value)}
                      className="px-3 py-1 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                    >
                      <option value="leads" className="text-gray-900">Leads</option>
                      <option value="customers" className="text-gray-900">Customers</option>
                      <option value="followups" className="text-gray-900">Follow-ups</option>
                      <option value="conversion" className="text-gray-900">Conversion Rate</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {analyticsData?.teamPerformance?.map((member, index) => (
                    <motion.div
                      key={member._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.1 * index }}
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200 space-y-3 sm:space-y-0"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-sm sm:text-base">
                            {member.firstName?.[0]}{member.lastName?.[0]}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                            {member.firstName} {member.lastName}
                          </h4>
                          <p className="text-xs sm:text-sm text-gray-600 capitalize">
                            {member.role?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end sm:space-x-6 w-full sm:w-auto">
                        <div className="text-center sm:text-right">
                          <p className="text-xl sm:text-2xl font-bold text-gray-900">
                            {getMetricValue(selectedMetric, member)}
                          </p>
                          <p className="text-xs text-gray-500 capitalize">{selectedMetric}</p>
                        </div>
                        <div className="flex items-center ml-4 sm:ml-0">
                          {member.trend === 'up' ? (
                            <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
                          ) : (
                            <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Recent Activity */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 sm:px-6 py-4">
                <h3 className="text-lg sm:text-xl font-bold text-white">Recent Activity</h3>
              </div>

              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {analyticsData?.recentActivity?.map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="p-4"
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`flex-shrink-0 p-2 rounded-lg ${activity.color}`}>
                        {React.createElement(getActivityIcon(activity.type), { className: "h-4 w-4" })}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                        <p className="text-sm text-gray-600">{activity.target}</p>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-500">{activity.user}</p>
                          <p className="text-xs text-gray-500">{activity.time}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Conversion Funnel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 py-4">
              <div className="flex items-center space-x-3">
                <FunnelIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                <h3 className="text-lg sm:text-xl font-bold text-white">Conversion Funnel</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {analyticsData?.conversionFunnel?.map((stage, index) => (
                  <motion.div
                    key={stage.stage}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 * index }}
                    className="relative"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 space-y-2 sm:space-y-0">
                      <div className="flex flex-wrap items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{ backgroundColor: stage.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
                        {stage.isWon && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Won
                          </span>
                        )}
                        {stage.isLost && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Lost
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-lg font-bold text-gray-900">{stage.count}</span>
                        <span className="text-sm text-gray-500">{stage.percentage}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${stage.percentage}%` }}
                        transition={{ duration: 1, delay: 0.2 * index }}
                        className="h-3 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default TeamAnalytics;